import {
  Alert,
  Stack,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { FormsIds, NOTIFICATION_CONFIG } from "../../Utils/Constants";
import { Vocabulary, replacePlaceholders } from "../../Utils/Vocabulary";
import genericStyles from "../../Styles/genericStyles.module.css";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { endpoints } from "../../Utils/UrlEnum";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { onFormError } from "../../Utils/Utils";
import { NotificationConfigurationFormProps } from "../../Utils/Types";
import SwitchField from "../FormFields/SwitchField";

const ATESTAT_EXPIRY_REMINDER = "ATESTAT_EXPIRY_REMINDER";

type WorkPoint = {
  _id: string;
  name: string;
};

/**
 * A React component that renders a notification configuration form.
 * This component is used for both creating and editing notification configurations.
 *
 * @param {NotificationConfigurationFormProps} props - The props object containing the following properties:
 * @param {NotificationConfiguration | any} selectedRow - The notification configuration to be edited.
 * @param {() => void} onClose - The function to call when the form is closed.
 * @param {any[]} notificationTypes - The available notification types.
 * @param {any[]} users - The available users.
 */
export default function NotificationConfigurationForm(
  props: NotificationConfigurationFormProps
) {
  const { selectedRow, onClose, notificationTypes, users } = props;
  const { control, watch, handleSubmit, reset, setValue } = useForm({
    defaultValues: NOTIFICATION_CONFIG,
  });

  const [workPoints, setWorkPoints] = useState<WorkPoint[]>([]);

  const memo = useCustomMemo();
  const roles =
    memo?.storage?.roles?.filter(
      (r: any) =>
        r.name != Vocabulary.subscription && r.name != Vocabulary.userRole
    ) || [];

  const selectedRoleIds = (watch("roles") || []) as string[];
  const selectedUserIds = (watch("selectedUsers") || []) as string[];
  const notificationType = watch("notificationType") as string;

  const filteredUsers = users.filter((user) =>
    user.roleIds?.some((role: any) =>
      selectedRoleIds.includes(role._id || role)
    )
  );

  /**
   * Sets the form values when a row is selected for editing.
   */
  useEffect(() => {
    if (selectedRow) {
      reset(selectedRow);
    }
  }, [selectedRow, reset]);

  /**
   * Fetches work points when the selected notification type is ATESTAT_EXPIRY_REMINDER.
   * For creates (no selectedRow), pre-populates workPointMessages with empty strings.
   * For edits, the work points list is used only to resolve names; values come from reset().
   */
  useEffect(() => {
    if (notificationType !== ATESTAT_EXPIRY_REMINDER) {
      setWorkPoints([]);
      return;
    }

    getData(endpoints.workPoints.main).then((res: AxiosResponse) => {
      if (!res || res?.data?.errors) return;

      const fetched: WorkPoint[] = res.data || [];
      setWorkPoints(fetched);

      // Only pre-populate when creating a new config (no _id yet).
      // For edits, reset() has already loaded the saved workPointMessages.
      if (!selectedRow?._id) {
        const initialMessages = fetched.map((wp) => ({
          workPointId: wp._id,
          smsMessage: "",
          emailMessage: "",
        }));
        setValue("workPointMessages" as any, initialMessages);
      }
    });
  }, [notificationType, selectedRow, setValue]);

  /**
   * Create a new notification configuration.
   *
   * @param {Object} model - The new notification configuration.
   *
   * @returns {Promise<void>} - A promise that resolves when the notification configuration is created, or rejects if there is an error.
   */
  const createElement = (model: any) => {
    postData(endpoints.notifications.configurations, model).then(
      (res: AxiosResponse) => {
        if (!res || res?.data?.errors) {
          return;
        }
        onClose();
      }
    );
  };

  /**
   * Update an existing notification configuration.
   *
   * @param {Object} model - The notification configuration to update.
   *
   * @returns {Promise<void>} - A promise that resolves when the notification configuration is updated, or rejects if there is an error.
   */
  const updateElement = (model: any) => {
    updateData(
      `${endpoints.notifications.configurations}/${model._id}`,
      model
    ).then((res: AxiosResponse) => {
      if (!res || res?.data?.errors) {
        return;
      }
      onClose();
    });
  };

  /**
   * Submits the form with the given model. If the model has an _id
   * property, it calls updateElement, otherwise it calls createElement.
   * @param {Object} model - The model to be submitted.
   */
  const onSubmit = (model: any) => {
    model._id ? updateElement(model) : createElement(model);
  };

  /**
   * Renders the notification configuration form.
   */
  return (
    <form
      id={FormsIds.notificationForm}
      className={genericStyles.form}
      onSubmit={handleSubmit(onSubmit, onFormError)}
    >
      <Stack spacing={2}>
        <Controller
          name="notificationType"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Autocomplete
              freeSolo={false}
              disablePortal
              noOptionsText={Vocabulary.noResultsFound}
              fullWidth
              onChange={(_, value) => field.onChange(value?.key || "")}
              value={
                notificationTypes.find((type) => type.key === field.value) ||
                null
              }
              options={notificationTypes}
              isOptionEqualToValue={(option, value) => option.key === value}
              getOptionLabel={(option: any) => option.title}
              id="notificationType"
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!error}
                  helperText={error?.message}
                  name="notificationType"
                  size="small"
                  variant="outlined"
                  inputRef={ref}
                  label={Vocabulary.type}
                />
              )}
            />
          )}
        />

        <Controller
          name="roles"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Autocomplete
              freeSolo={false}
              disablePortal
              multiple
              noOptionsText={Vocabulary.noResultsFound}
              fullWidth
              getOptionDisabled={(option: any) =>
                !!watch("roles")?.find(
                  (element: any) => element._id === option._id
                )
              }
              onChange={(_, value) =>
                field.onChange(value?.map((v: any) => v._id))
              }
              value={
                roles?.filter((role: any) =>
                  selectedRoleIds?.includes(role._id)
                ) || []
              }
              options={roles}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              getOptionLabel={(option: any) => option.name}
              id="roles"
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!error}
                  helperText={error?.message}
                  name="roles"
                  size="small"
                  variant="outlined"
                  inputRef={ref}
                  label={Vocabulary.roles}
                />
              )}
            />
          )}
        />

        {selectedRoleIds.length > 0 && filteredUsers.length > 0 && (
          <>
            <Controller
              name="selectedUsers"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  multiple
                  noOptionsText={Vocabulary.noResultsFound}
                  fullWidth
                  getOptionDisabled={(option: any) =>
                    !!watch("selectedUsers")?.find(
                      (element: any) => element === option._id
                    )
                  }
                  onChange={(_, value) =>
                    field.onChange(value?.map((v: any) => v._id))
                  }
                  value={
                    filteredUsers?.filter((user: any) =>
                      selectedUserIds?.includes(user._id)
                    ) || []
                  }
                  options={filteredUsers}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === (value as any)?._id ||
                    option?._id === (value as any)
                  }
                  getOptionLabel={(option: any) =>
                    `${option.firstName} ${option.lastName}`
                  }
                  id="selectedUsers"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="selectedUsers"
                      size="small"
                      variant="outlined"
                      inputRef={ref}
                      label={Vocabulary.users}
                    />
                  )}
                />
              )}
            />

            <Alert severity="info">
              {selectedUserIds.length === 0
                ? Vocabulary.sendNotificationsAllRoles
                : replacePlaceholders(Vocabulary.sendNotificationsUsers, {
                    count: selectedUserIds.length,
                  })}
            </Alert>
          </>
        )}

        <SwitchField
          name="sendEmail"
          label={Vocabulary.sendEmail}
          control={control}
        />

        <SwitchField
          name="sendNotification"
          label={Vocabulary.sendNotification}
          control={control}
        />

        <SwitchField
          name="sendSMS"
          label={Vocabulary.sendSMS}
          control={control}
        />

        <SwitchField
          name="isActive"
          label={Vocabulary.active}
          control={control}
          color="success"
        />

        {notificationType === ATESTAT_EXPIRY_REMINDER &&
          workPoints.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {Vocabulary.messagesPerWorkPoint}
              </Typography>
              <Stack spacing={2}>
                {workPoints.map((wp, index) => (
                  <Card key={wp._id} variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        {wp.name}
                      </Typography>
                      <Stack spacing={2}>
                        <Controller
                          name={`workPointMessages.${index}.smsMessage` as any}
                          control={control}
                          defaultValue=""
                          render={({
                            field,
                            fieldState: { error },
                          }) => (
                            <TextField
                              {...field}
                              label={Vocabulary.smsMessage}
                              size="small"
                              variant="outlined"
                              fullWidth
                              multiline
                              minRows={2}
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                        <Controller
                          name={
                            `workPointMessages.${index}.emailMessage` as any
                          }
                          control={control}
                          defaultValue=""
                          render={({
                            field,
                            fieldState: { error },
                          }) => (
                            <TextField
                              {...field}
                              label={Vocabulary.emailMessage}
                              size="small"
                              variant="outlined"
                              fullWidth
                              multiline
                              minRows={3}
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                        {/* Hidden controller to carry workPointId in the submitted payload */}
                        <Controller
                          name={
                            `workPointMessages.${index}.workPointId` as any
                          }
                          control={control}
                          defaultValue={wp._id}
                          render={({ field }) => (
                            <input type="hidden" {...field} />
                          )}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
      </Stack>
    </form>
  );
}
